import { getApiConfig, isApiError } from '@pa/api'
import {
  createBooking,
  getEndSessionUrl,
  getGetPublicOptionsQueryOptions,
  lookupPatient,
  placeHold,
  recordPublicEvent,
  releaseHold,
  startSession,
  type BookingConfirmation,
  type BookingStatus,
  type LookupResult,
  type PublicHold,
  type PublicOptions,
} from '@pa/api/public'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { BotInputs } from '@/features/bot'
import { fetchClinicConfig, type LinkParams } from '@/features/clinic'
import { dobOf, type IdentifyFields } from '@/features/entry'
import type { Outcome } from '../model/machine'

/** Lookup: 20 s client timeout (the server's budget is 15 s, D-30; Frontend §4.8). */
export const LOOKUP_TIMEOUT_MS = 20_000
/** Reads and holds: 10 s; booking: 30 s (the server's booking budget is 25 s, D-30; Frontend §4.8). */
export const HOLD_TIMEOUT_MS = 10_000
export const BOOKING_TIMEOUT_MS = 30_000

/**
 * An outcome the machine understands. A `409 conflict` is matched on its reason (API.md §1.6): only
 * `option_unavailable` stays `conflict`; any other 409 falls back to `slot_taken` (P-08, the patient fallback).
 */
export const outcome = async <T>(run: () => Promise<T>): Promise<Outcome<T>> => {
  try {
    return { ok: true, value: await run() }
  } catch (e) {
    if (isApiError(e)) {
      if (e.code === 'conflict' && e.details?.reason !== 'option_unavailable') return { ok: false, code: 'slot_taken' }
      return { ok: false, code: e.code }
    }
    return { ok: false, code: 'network_error' }
  }
}

/** One `Idempotency-Key` per confirm attempt (UUIDv4, Frontend §4.10). */
export const newIdempotencyKey = (): string => crypto.randomUUID()

/** API.md §4.4: the P-01 choice, for the funnel (D-67). */
export type PublicEventType = 'existing_chosen' | 'new_chosen'

/**
 * The public calls of the booking flow (API.md #1, #2, #3, #4, #5, #6) as outcomes the machine understands. Every call
 * goes through `apiFetch` with `credentials: 'include'`; the app never reads or writes cookies (Frontend §4.4). Patient
 * details travel in the lookup's POST body only.
 */
export function usePatientApi(slug: string) {
  const queryClient = useQueryClient()
  return useMemo(
    () => ({
      /** #1, from the cache at boot; `fresh` for the "existing" re-check (API.md §4.1). */
      config: (fresh = false): Promise<Outcome<BookingStatus>> =>
        outcome(async () => (await fetchClinicConfig(queryClient, slug, fresh)).bookingStatus),

      /**
       * #2 with the raw link parameters (the server normalises the source). The embed (task 08) sends
       * `entry: 'embed'`, its `data-source` as `src`, never `existing=1`, and the embed-origin signal (weak, D-36).
       */
      startSession: (link: LinkParams, embed?: { embedOrigin: string | null }): Promise<Outcome<null>> =>
        outcome(async () => {
          // The C-02 link preset (task 22, D-79), raw: only sent when the link or `data-preset` had one.
          const preset = link.preset ? { preset: link.preset } : {}
          await startSession(
            slug,
            embed
              ? {
                  src: link.src,
                  utmSource: null,
                  entry: 'embed',
                  existingOnly: false,
                  embedOrigin: embed.embedOrigin,
                  ...preset,
                }
              : { src: link.src, utmSource: link.utmSource, entry: 'link', existingOnly: link.existingOnly, ...preset },
          )
          return null
        }),

      /**
       * #4 (API.md §4.4, task 18): fire-and-forget, never awaited by the UI; any failure, `session_expired` included,
       * is ignored (the next real call shows P-16 if the visit is gone).
       */
      event: (type: PublicEventType): void => {
        void recordPublicEvent(slug, { type }).catch(() => undefined)
      },

      /** #5: the typed names and DOB in the body only, with the bot inputs (API.md §1.12). */
      lookup: (fields: IdentifyFields, bot: BotInputs): Promise<Outcome<LookupResult>> =>
        outcome(() =>
          lookupPatient(
            slug,
            { firstName: fields.first, lastName: fields.last, dob: dobOf(fields), bot },
            {
              timeoutMs: LOOKUP_TIMEOUT_MS,
            },
          ),
        ),

      /** #6, never cached across patient sessions. */
      options: (): Promise<Outcome<PublicOptions>> =>
        outcome(() => queryClient.query({ ...getGetPublicOptionsQueryOptions(slug), staleTime: 0 })),

      /** #8: a 5-minute hold on a slot start from availability (the server derives everything else). */
      placeHold: (body: {
        locationId: string
        visitTypeId: string
        providerId: string
        start: string
      }): Promise<Outcome<PublicHold>> => outcome(() => placeHold(slug, body, { timeoutMs: HOLD_TIMEOUT_MS })),

      /**
       * #9, not awaited by the UI: a 404 (another visit's hold) is treated as done (API.md §4.9). No `reason` (P-14):
       * not a Back or an expiry for the funnel.
       */
      releaseHold: (holdId: string, reason?: 'back' | 'expired'): void => {
        void releaseHold(slug, holdId, reason ? { reason } : {}).catch(() => undefined)
      },

      /**
       * #10 with this attempt's `Idempotency-Key`; the reason travels in the POST body only (D-22). `timeoutMs` is what
       * is left of the attempt's 30 s budget (Frontend §4.10 (b)).
       */
      book: (
        holdId: string,
        reason: string | null,
        bot: BotInputs,
        key: string,
        timeoutMs: number = BOOKING_TIMEOUT_MS,
      ): Promise<Outcome<BookingConfirmation>> =>
        outcome(() => createBooking(slug, { holdId, reason, bot }, { headers: { 'Idempotency-Key': key }, timeoutMs })),

      /**
       * #3 on `pagehide` (Frontend §4.4 step 6): `navigator.sendBeacon` with an empty `text/plain` body, which carries
       * the cookies and needs no preflight. Fire and forget.
       */
      endBeacon: (): void => {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return
        const url = `${getApiConfig().baseUrl.replace(/\/+$/, '')}${getEndSessionUrl(slug)}`
        navigator.sendBeacon(url, new Blob([''], { type: 'text/plain' }))
      },
    }),
    [queryClient, slug],
  )
}
